// ============================================
// useDriverTracking — Production-Grade GPS Tracking Hook
// Yango/InDrive-level: Kalman-filtered, speed-adaptive,
// smooth heading, enriched socket payloads
// ============================================

import { useState, useEffect, useRef, useCallback } from 'react';
import socketService from '../services/socket';

/**
 * Simple 1D Kalman filter for GPS coordinate smoothing.
 * Eliminates jitter when stationary, preserves responsiveness when moving.
 */
class KalmanFilter {
    constructor(processNoise = 0.00001, measurementNoise = 0.0001) {
        this.Q = processNoise;      // Process noise (how much we expect real movement)
        this.R = measurementNoise;  // Measurement noise (GPS accuracy variance)
        this.P = 1;                 // Estimation error covariance
        this.X = null;              // Estimated value
        this.K = 0;                 // Kalman gain
    }

    filter(measurement) {
        if (this.X === null) {
            this.X = measurement;
            return measurement;
        }

        // Prediction
        this.P = this.P + this.Q;

        // Update
        this.K = this.P / (this.P + this.R);
        this.X = this.X + this.K * (measurement - this.X);
        this.P = (1 - this.K) * this.P;

        return this.X;
    }

    // Adjust noise based on speed — when moving fast, trust GPS more
    setSpeed(speedMs) {
        if (speedMs > 10) {
            // Highway speed: trust GPS heavily
            this.R = 0.00002;
            this.Q = 0.0001;
        } else if (speedMs > 3) {
            // City driving: balanced
            this.R = 0.00005;
            this.Q = 0.00005;
        } else if (speedMs > 0.5) {
            // Walking/slow: moderate filtering (more responsive than before)
            this.R = 0.00005;
            this.Q = 0.00005;
        } else {
            // Stationary: heavy filtering (eliminate jitter)
            this.R = 0.0005;
            this.Q = 0.000001;
        }
    }
}

const useDriverTracking = (userId, enabled = true, emitInterval = 1200) => {
    const [location, setLocation] = useState(null);
    const [heading, setHeading] = useState(0);
    const [accuracy, setAccuracy] = useState(null);
    const [speed, setSpeed] = useState(0);
    const [isTracking, setIsTracking] = useState(false);

    const locationRef = useRef(null);
    const headingRef = useRef(0);
    const speedRef = useRef(0);
    const lastEmitRef = useRef(0);
    const userIdRef = useRef(userId);
    const lastHeadingPosRef = useRef(null);
    const kalmanLatRef = useRef(new KalmanFilter());
    const kalmanLngRef = useRef(new KalmanFilter());

    userIdRef.current = userId;

    useEffect(() => {
        userIdRef.current = userId;
    }, [userId]);

    // ── Haversine distance in meters ──
    const distanceMeters = useCallback((a, b) => {
        const R = 6371000;
        const dLat = (b.lat - a.lat) * Math.PI / 180;
        const dLng = (b.lng - a.lng) * Math.PI / 180;
        const sinLat = Math.sin(dLat / 2);
        const sinLng = Math.sin(dLng / 2);
        const x = sinLat * sinLat +
            Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * sinLng * sinLng;
        return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
    }, []);

    // ── Compute bearing A → B ──
    const computeBearing = useCallback((from, to) => {
        const dLng = (to.lng - from.lng) * Math.PI / 180;
        const lat1 = from.lat * Math.PI / 180;
        const lat2 = to.lat * Math.PI / 180;
        const y = Math.sin(dLng) * Math.cos(lat2);
        const x = Math.cos(lat1) * Math.sin(lat2) -
            Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
        return ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360;
    }, []);

    // ── Emit to server with enriched payload ──
    const emitToServer = useCallback((lat, lng, hdg, spd) => {
        const now = Date.now();
        // Speed-adaptive emit: faster when moving, slower when stationary
        const adaptiveInterval = spd > 5 ? Math.max(800, emitInterval - 400)
            : spd > 1 ? emitInterval
            : emitInterval + 500;

        if (now - lastEmitRef.current < adaptiveInterval) return;
        lastEmitRef.current = now;

        if (userIdRef.current) {
            socketService.emit('driver:location', {
                userId: userIdRef.current,
                lat,
                lng,
                heading: Math.round(hdg || 0),
                speed: Math.round((spd || 0) * 3.6), // m/s → km/h
                timestamp: now,
            });
        }
    }, [emitInterval]);

    // ── Device Orientation for Stationary Heading ──
    useEffect(() => {
        if (!enabled) return;

        const handleOrientation = (event) => {
            // Speed must be very low (<1 m/s) to trust compass over GPS
            if (speedRef.current > 1) return;

            let newHeading = null;

            if (event.webkitCompassHeading !== undefined) {
                // iOS
                newHeading = event.webkitCompassHeading;
            } else if (event.absolute === true && event.alpha !== null) {
                // Android absolute orientation
                newHeading = (360 - event.alpha) % 360;
            }

            if (newHeading !== null) {
                const diff = Math.abs(newHeading - headingRef.current);
                const wrapped = Math.min(diff, 360 - diff);
                
                // Update if rotated by more than 5 degrees to avoid micro-jitter and spamming the server
                if (wrapped > 5) {
                    headingRef.current = newHeading;
                    setHeading(newHeading);

                    // Send the new heading to the server immediately so it syncs to dispatch/admin
                    if (locationRef.current) {
                        // Bypass normal rate limit for orientation changes by forcing a timestamp override
                        // or just calling emitToServer which has its own rate limit (so we don't spam)
                        emitToServer(
                            locationRef.current.lat,
                            locationRef.current.lng,
                            newHeading,
                            speedRef.current
                        );
                    }
                }
            }
        };

        window.addEventListener('deviceorientationabsolute', handleOrientation);
        window.addEventListener('deviceorientation', handleOrientation);

        return () => {
            window.removeEventListener('deviceorientationabsolute', handleOrientation);
            window.removeEventListener('deviceorientation', handleOrientation);
        };
    }, [enabled, emitToServer]);

    // ── GPS watchPosition with Kalman filtering ──
    useEffect(() => {
        if (!enabled || !('geolocation' in navigator)) return;

        let watchId = null;
        let heartbeatId = null;

        watchId = navigator.geolocation.watchPosition(
            (position) => {
                const { latitude, longitude, heading: gpsHdg, speed: gpsSpeed, accuracy: gpsAcc } = position.coords;
                const rawSpeed = gpsSpeed || 0;

                // Adapt Kalman filter noise to current speed
                kalmanLatRef.current.setSpeed(rawSpeed);
                kalmanLngRef.current.setSpeed(rawSpeed);

                // Apply Kalman filter
                const filteredLat = kalmanLatRef.current.filter(latitude);
                const filteredLng = kalmanLngRef.current.filter(longitude);
                const currentPos = { lat: filteredLat, lng: filteredLng };

                let bestHeading = headingRef.current;

                // Priority 1: Hardware GPS heading (reliable at speed > 1 m/s)
                if (gpsHdg !== null && !isNaN(gpsHdg) && rawSpeed > 1) {
                    bestHeading = gpsHdg;
                    lastHeadingPosRef.current = currentPos;
                } else {
                    // Priority 2: Compute heading from position delta (threshold 1.5m)
                    if (lastHeadingPosRef.current) {
                        const dist = distanceMeters(lastHeadingPosRef.current, currentPos);
                        if (dist >= 1.5) {
                            bestHeading = computeBearing(lastHeadingPosRef.current, currentPos);
                            lastHeadingPosRef.current = currentPos;
                        }
                    } else {
                        lastHeadingPosRef.current = currentPos;
                    }
                }

                // Always update state
                locationRef.current = currentPos;
                headingRef.current = bestHeading;
                speedRef.current = rawSpeed;

                setLocation({ ...currentPos });
                setHeading(bestHeading);
                setAccuracy(gpsAcc);
                setSpeed(rawSpeed);
                setIsTracking(true);

                emitToServer(filteredLat, filteredLng, bestHeading, rawSpeed);
            },
            (error) => {
                console.warn('GPS error:', error.message);
                setIsTracking(false);
            },
            { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
        );

        // Heartbeat: ensure server always has fresh data (even if GPS fires slowly)
        heartbeatId = setInterval(() => {
            if (locationRef.current && userIdRef.current) {
                socketService.emit('driver:location', {
                    userId: userIdRef.current,
                    lat: locationRef.current.lat,
                    lng: locationRef.current.lng,
                    heading: Math.round(headingRef.current),
                    speed: Math.round((speedRef.current || 0) * 3.6),
                    timestamp: Date.now(),
                });
            }
        }, 3000);

        return () => {
            if (watchId !== null) navigator.geolocation.clearWatch(watchId);
            if (heartbeatId) clearInterval(heartbeatId);
        };
    }, [enabled, distanceMeters, computeBearing, emitToServer]);

    // ── Socket fallback: receive location from other devices ──
    // When driver opens dashboard on a laptop (no GPS movement),
    // listen for location updates broadcast from the phone via server
    useEffect(() => {
        if (!enabled || !userId) return;

        const handleRemoteLocation = (data) => {
            // Only process our own location updates
            if (data.userId !== userIdRef.current) return;

            // If local GPS is actively updating and moving, ignore remote data
            // (local GPS is authoritative on the device that's actually moving)
            const localPos = locationRef.current;
            if (localPos) {
                const dLat = Math.abs(data.lat - localPos.lat);
                const dLng = Math.abs(data.lng - localPos.lng);
                // If local and remote are very close (< ~5m), local GPS is fine, skip remote
                if (dLat < 0.00005 && dLng < 0.00005) return;
            }

            // Remote data is significantly different from local → use it
            // This means the phone is moving but the laptop GPS is static
            const remotePos = { lat: data.lat, lng: data.lng };
            const remoteHeading = data.heading || 0;
            const remoteSpeed = (data.speed || 0) / 3.6; // km/h → m/s

            locationRef.current = remotePos;
            headingRef.current = remoteHeading;
            speedRef.current = remoteSpeed;

            setLocation({ ...remotePos });
            setHeading(remoteHeading);
            setSpeed(remoteSpeed);
            setIsTracking(true);
        };

        socketService.on('driver:location:update', handleRemoteLocation);

        return () => {
            socketService.off('driver:location:update', handleRemoteLocation);
        };
    }, [enabled, userId]);

    return { location, heading, accuracy, speed, isTracking };
};

export default useDriverTracking;
